任务管理系统首页组件（优化后）

<template>
  <div class="home-container" :class="{ 'wxwork-mobile': isWxWorkMobile }">
    <!-- 顶部导航栏：移动端隐藏 -->
    <el-header class="home-header" v-if="!isWxWorkMobile">
      <div class="header-left">
        <i class="el-icon-menu menu-toggle" @click="toggleSidebar"></i>
        <span class="system-title">任务管理系统</span>
      </div>
      <div class="header-right">
        <el-dropdown @command="handleDropdownCommand">
          <span class="admin-info">
            <i class="el-icon-user"></i>
            {{ adminName }}
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="logout">
              <i class="el-icon-switch-button"></i>
              退出登录
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </el-header>

    <!-- 主体内容：移动端仅显示内容区域 -->
    <el-container class="home-main">
      <el-aside :width="sidebarWidth" class="home-aside" v-if="!isWxWorkMobile">
        <!-- 核心：v-bind绑定default-active，避免ESLint解析报错 -->
        <el-menu
          :default-active="isWxWorkLogin ? '/tasks' : '/excel/import'"
          class="el-menu-vertical-demo"
          background-color="#2e3b4e"
          text-color="#fff"
          active-text-color="#ffd04b"
          :collapse="isSidebarCollapse"
          router
        >
          <el-submenu index="1">
            <template slot="title">
              <i class="el-icon-list"></i>
              <span>任务管理</span>
            </template>
            <!-- 仅企微登录显示：任务列表 -->
            <el-menu-item index="/tasks" v-if="isWxWorkLogin">
              <i class="el-icon-document"></i>
              任务列表
            </el-menu-item>
            <!-- 仅企微登录显示：创建任务 -->
            <el-menu-item index="/task/create" v-if="isWxWorkLogin">
              <i class="el-icon-plus"></i>
              创建任务
            </el-menu-item>
            <!-- 所有人可见：基础表数据导入 -->
            <el-menu-item index="/excel/import">
              <i class="el-icon-upload2"></i>
              基础表数据导入
            </el-menu-item>
            <!-- 所有人可见：业务汇总表导出 -->
            <el-menu-item index="/excel/export">
              <i class="el-icon-download"></i>
              业务汇总表导出
            </el-menu-item>
            <!-- 新增优化菜单项 -->
            <el-menu-item index="/excel/import/store-business">
              <i class="el-icon-upload2"></i>
              店面业务数据导入
            </el-menu-item>
            <el-menu-item index="/excel/import/store-auxiliary">
              <i class="el-icon-upload2"></i>
              店面辅助表导入
            </el-menu-item>
            <el-menu-item index="/excel/import/serial-code">
              <i class="el-icon-upload2"></i>
              串码单独导入
            </el-menu-item>
            <el-menu-item index="/excel/export/store-all">
              <i class="el-icon-download"></i>
              店面业务汇总数据导出
            </el-menu-item>
            <el-menu-item index="/excel/export/check-exception">
              <i class="el-icon-download"></i>
              核对异常数据导出
            </el-menu-item>
          </el-submenu>
          <el-submenu index="2">
            <template slot="title">
              <i class="el-icon-setting"></i>
              <span>系统设置</span>
            </template>
            <el-menu-item index="/system/user">
              <i class="el-icon-user"></i>
              用户管理
            </el-menu-item>
          </el-submenu>
        </el-menu>
      </el-aside>
      <el-main class="home-content">
        <el-card class="content-card">
          <router-view />
        </el-card>
      </el-main>
    </el-container>
  </div>
</template>

<script>
import { isWxWorkEnv } from '@/utils/request';
import { MessageBox, Message } from 'element-ui';
import router from '@/router';

export default {
  name: 'Home',
  data() {
    return {
      adminName: '管理员',
      isSidebarCollapse: false,
      sidebarWidth: '200px',
      isWxWorkMobile: false,
      isWxWorkLogin: false // 控制菜单显示的核心变量
    };
  },
  created() {
    // 初始化企微移动端判断，增加容错
    this.isWxWorkMobile = isWxWorkEnv() && /mobile/i.test(navigator.userAgent) || false;
    // 核心：从localStorage获取登录类型，判断是否为企微登录（增加多层容错，避免undefined报错）
    const loginType = localStorage.getItem('loginType') || '';
    this.isWxWorkLogin = loginType === 'wxwork';
  },
  mounted() {
    // 初始化用户名，增加多层容错，避免空值/undefined报错
    const userName = localStorage.getItem('adminName');
    if (userName && userName.trim() && userName !== 'undefined') {
      this.adminName = userName.trim();
    }
  },
  methods: {
    // 侧边栏折叠/展开
    toggleSidebar() {
      this.isSidebarCollapse = !this.isSidebarCollapse;
      this.sidebarWidth = this.isSidebarCollapse ? '64px' : '200px';
    },
    // 下拉菜单命令处理
    handleDropdownCommand(command) {
      if (command === 'logout') {
        this.handleLogout();
      }
    },
    // 退出登录：无后台请求，仅清除本地凭证+跳转（稳定版）
    async handleLogout() {
      try {
        const confirmRes = await MessageBox.confirm(
          '确定要退出登录吗？',
          '退出确认',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        );

        // 仅在用户确认后清除凭证（避免误操作）
        if (confirmRes === 'confirm') {
          // 彻底清除所有登录相关凭证，包括登录类型
          localStorage.removeItem('accessToken');
          localStorage.removeItem('adminName');
          localStorage.removeItem('loginType');
          sessionStorage.clear();
          // 可选：清空所有localStorage（若无需保留其他数据）
          // window.localStorage.clear();

          // 跳转登录页：区分企微/普通环境，修复模板字符串为字符串拼接
          const isWxWork = isWxWorkEnv();
          if (isWxWork) {
            window.location.href = '/api/wxwork/auth?redirect=' + encodeURIComponent(window.location.origin);
          } else {
            // 普通环境：路由跳转+重定向兜底，避免路由报错
            try {
              await router.push({ path: '/login', replace: true });
            } catch (err) {
              window.location.href = '/login';
            }
          }

          Message.success('退出登录成功！');
        }
      } catch (error) {
        // 仅过滤用户取消操作，其他异常轻量提示
        if (error !== 'cancel' && error !== 'close') {
          Message.error('退出操作异常，请重试！');
          console.error('退出登录异常：', error);
        }
      }
    }
  }
};
</script>

<style scoped>
/* 全局容器：清除默认边距，避免布局错乱 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
.home-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
/* 企微移动端专属样式 */
.wxwork-mobile {
  height: 100%;
  overflow-y: auto;
}
.wxwork-mobile .home-content {
  padding: 10px !important;
  background-color: #fff !important;
  padding-bottom: 70px !important;
}
.wxwork-mobile .content-card {
  box-shadow: none !important;
  border: none !important;
}

/* 顶部导航栏 */
.home-header {
  background-color: #fff;
  color: #333;
  border-bottom: 1px solid #e6e6e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  height: 60px;
}
.header-left {
  display: flex;
  align-items: center;
}
.menu-toggle {
  font-size: 20px;
  cursor: pointer;
  margin-right: 15px;
  color: #2e3b4e;
}
.system-title {
  font-size: 18px;
  font-weight: bold;
  color: #2e3b4e;
}
.header-right {
  display: flex;
  align-items: center;
}
.admin-info {
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0 10px;
  height: 60px;
  color: #333;
}

/* 主体内容容器 */
.home-main {
  flex: 1;
  display: flex;
  overflow: hidden;
}
/* 侧边栏 */
.home-aside {
  background-color: #2e3b4e;
  transition: width 0.3s ease;
  overflow: hidden;
}
/* 主内容区域 */
.home-content {
  padding: 20px;
  background-color: #f5f5f5;
  flex: 1;
  overflow: auto;
}
.content-card {
  height: 100%;
  border: none;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

/* 媒体查询：适配非企微端手机屏幕 */
@media (max-width: 768px) {
  .home-header {
    padding: 0 10px;
  }
  .system-title {
    font-size: 16px;
  }
  .home-content {
    padding: 10px;
  }
}
</style>
